import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { FileText } from 'lucide-react';

const SUBMITTER_KEYS = ['submitted_by', 'Submitted_By', 'submitter', 'Submitter', 'submitter_name', 'Submitter_Name', 'user_name', 'User_Name', 'Name', 'name', 'email', 'Email'];

function findSubmitter(sub) {
  // First check if backend resolved it from form config
  if (sub.submitter_name) return sub.submitter_name;
  // Fallback: guess from payload
  const payload = sub.raw_payload;
  if (!payload) return 'Unknown';
  for (const key of SUBMITTER_KEYS) {
    if (payload[key]) return String(payload[key]);
  }
  return 'Unknown';
}

function timeAgo(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now - d;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  const days = Math.floor(hrs / 24);
  return `${days} day${days > 1 ? 's' : ''} ago`;
}

function StatusBadge({ status }) {
  const map = {
    success: 'default',
    partial: 'secondary',
    failed: 'destructive',
    duplicate: 'outline',
    test: 'secondary',
  };
  return <Badge variant={map[status] || 'outline'} className="text-xs">{status}</Badge>;
}

export default function ZohoDashboardRecent({ submissions }) {
  return (
    <Card data-testid="recent-submissions-section">
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-semibold">Recent Submissions</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {submissions.length === 0 ? (
          <div className="text-center py-10 text-muted-foreground">
            <FileText className="h-8 w-8 mx-auto mb-2 opacity-20" />
            <p className="text-sm">No submissions yet</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Form</TableHead>
                <TableHead>Submitter</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">When</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {submissions.map(s => (
                <TableRow key={s.id} data-testid={`recent-sub-${s.id}`}>
                  <TableCell className="text-sm font-medium max-w-[200px] truncate">{s.form_name || '—'}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{findSubmitter(s)}</TableCell>
                  <TableCell><StatusBadge status={s.processing_status} /></TableCell>
                  <TableCell className="text-right text-xs text-muted-foreground">{timeAgo(s.created_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
